/**
 * OpenalexRetrievalProgress
 *
 * Polls /api/openalex-retrieval/jobs/{jobId} every 3 s and shows a live progress view.
 * On completion the user can navigate back to the sub-project. Mirrors OpsRetrievalProgress.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openalexRetrievalService, type OpenAlexRetrievalJob } from '../services/openalexRetrievalService';
import { formatDuration } from '../services/opsRetrievalService';

const POLL_INTERVAL_MS = 3000;

export default function OpenalexRetrievalProgress() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [job, setJob] = useState<OpenAlexRetrievalJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJob = async () => {
    if (!jobId) return;
    try {
      const data = await openalexRetrievalService.getJobStatus(jobId);
      setJob(data);
      if (['completed', 'failed', 'cancelled'].includes(data.status)) {
        stopPolling();
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load job');
      stopPolling();
    }
  };

  const startPolling = () => {
    fetchJob();
    intervalRef.current = setInterval(fetchJob, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    startPolling();
    return stopPolling;
  }, [jobId]);

  const handleCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await openalexRetrievalService.cancelJob(jobId);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const handleViewResults = () => {
    if (!job) return;
    navigate(`/scv2/sub-project/${job.sub_project_id}`);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-sm text-red-600">{error}</p>
        <button className="text-sm text-slate-500 hover:underline" onClick={() => navigate(-1)}>
          {t('common.back', 'Back')}
        </button>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p>
      </div>
    );
  }

  const { progress, status, estimated_duration_seconds, filter, error_details } = job;
  const { total_works, processed_works, failed_works } = progress;
  const pct = total_works > 0 ? Math.round((processed_works / total_works) * 100) : 0;

  const elapsedEstimate = (() => {
    if (!job.started_at) return null;
    const elapsed = (Date.now() - new Date(job.started_at).getTime()) / 1000;
    const remaining = estimated_duration_seconds - elapsed;
    return remaining > 0 ? remaining : 0;
  })();

  const statusLabel: Record<OpenAlexRetrievalJob['status'], string> = {
    pending: t('openalexRetrieval.progress.pending', 'Pending…'),
    running: t('openalexRetrieval.progress.running', 'Running'),
    completed: t('openalexRetrieval.progress.completed', 'Completed'),
    failed: t('openalexRetrieval.progress.failed', 'Failed'),
    cancelled: t('openalexRetrieval.progress.cancelled', 'Cancelled'),
    cancelling: t('openalexRetrieval.progress.cancelling', 'Cancelling…'),
  };

  const isActive = status === 'running' || status === 'pending';
  const isDone = status === 'completed';
  const isError = status === 'failed';

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <h1 className="text-lg font-semibold text-slate-800">
        {t('openalexRetrieval.progress.title', 'OpenAlex Literature Retrieval')}
        {' — '}
        <span className={`text-sm font-normal ${isError ? 'text-red-600' : isDone ? 'text-green-600' : 'text-slate-500'}`}>
          {statusLabel[status]}
        </span>
      </h1>

      {elapsedEstimate !== null && isActive && (
        <div className="text-xs text-slate-500">
          {t('openalexRetrieval.progress.eta', 'ETA')}: ~{formatDuration(elapsedEstimate)}
        </div>
      )}

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{processed_works.toLocaleString()} / {total_works.toLocaleString()} {t('openalexRetrieval.progress.works', 'works')}</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${isError ? 'bg-red-400' : isDone ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {failed_works > 0 && (
          <p className="text-xs text-amber-600 mt-1">
            {failed_works} {t('openalexRetrieval.progress.failedWorks', 'works could not be retrieved')}
          </p>
        )}
      </div>

      {/* Filter summary */}
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
          {t('openalexRetrieval.progress.showFilter', 'Show OpenAlex filter')}
        </summary>
        <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded font-mono whitespace-pre-wrap break-all text-slate-600">{filter}</pre>
      </details>

      {isError && error_details && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error_details}</p>
      )}

      <div className="flex gap-3 flex-wrap">
        {isDone && (
          <button className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" onClick={handleViewResults}>
            {t('openalexRetrieval.progress.viewResults', 'View Results')}
          </button>
        )}
        {isActive && (
          <button
            className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
            disabled={cancelling || job.status === 'cancelling'}
            onClick={handleCancel}
          >
            {cancelling || job.status === 'cancelling'
              ? t('openalexRetrieval.progress.cancelling', 'Cancelling…')
              : t('openalexRetrieval.progress.cancelBtn', 'Cancel')}
          </button>
        )}
        <button className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200" onClick={() => navigate(-1)}>
          {t('common.back', 'Back')}
        </button>
      </div>
    </div>
  );
}
